/**
 * Locked Dashboard Component
 * Shows when user has < 10 analyses (lite_docs)
 * Displays progress and encourages user to add more analyses
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';

interface LockedDashboardProps {
  currentCount: number;
  requiredCount?: number;
  recentAnalyses?: Array<{
    id: string;
    text_snippet: string;
    date: string;
    sentiment_summary?: string;
    metadata?: {
      source?: 'email' | 'dashboard';
      subject?: string;
      original_date?: string;
      forwarded_date?: string;
    };
  }>;
}

export const LockedDashboard: React.FC<LockedDashboardProps> = ({
  currentCount,
  requiredCount = 10,
  recentAnalyses = [],
}) => {
  const navigate = useNavigate();
  const remaining = Math.max(0, requiredCount - currentCount);
  const progressPercentage = Math.round((currentCount / requiredCount) * 100);
  
  // Calculate stroke-dasharray for circular progress (circumference = 2 * PI * 45 ≈ 282.7)
  const circumference = 282.7;
  const strokeDasharray = `${(progressPercentage / 100) * circumference} ${circumference}`;

  return (
    <div className="min-h-[calc(100vh-180px)] flex items-center justify-center p-4">
      {/* Background blur effect */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-50/50 via-transparent to-gray-50 dark:from-gray-900/50 dark:to-gray-900"></div>
      </div>

      {/* Main card */}
      <div className="relative z-10 w-full max-w-[520px] bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Orange accent line */}
        <div className="h-1.5 w-full bg-gradient-to-r from-orange-500 to-orange-600"></div>
        
        <div className="px-8 pt-10 pb-8 flex flex-col items-center text-center">
          {/* Icon */}
          <div className="mb-6 relative">
            <div className="absolute -inset-4 bg-orange-500/10 rounded-full blur-lg"></div>
            <svg 
              className="w-16 h-16 text-orange-500 relative z-10" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" 
              />
            </svg>
          </div>

          {/* Headline */}
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Building your Footprint
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 font-medium tracking-wide uppercase mb-8">
            Data Collection Phase
          </p>

          {/* Circular Progress */}
          <div className="relative w-40 h-40 mb-8 flex items-center justify-center">
            <svg className="transform -rotate-90 w-full h-full" viewBox="0 0 100 100">
              {/* Background circle */}
              <circle
                cx="50"
                cy="50"
                r="45"
                fill="none"
                stroke="#f3ede7"
                className="dark:stroke-gray-700"
                strokeWidth="8"
              />
              {/* Progress circle */}
              <circle
                cx="50"
                cy="50"
                r="45"
                fill="none"
                stroke="#ec7f13"
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={strokeDasharray}
                className="transition-all duration-1000 ease-out"
              />
            </svg>
            
            {/* Center text */}
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-4xl font-bold text-gray-900 dark:text-white">
                {currentCount}
                <span className="text-gray-400 text-2xl">/{requiredCount}</span>
              </span>
              <span className="text-[10px] font-bold uppercase text-gray-400 mt-1">
                Interactions
              </span>
            </div>
          </div>

          {/* Explanation */}
          <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-5 mb-8 border border-orange-100 dark:border-orange-900/30 w-full">
            <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
              We need <strong className="text-orange-600 dark:text-orange-500">{remaining} more interaction{remaining !== 1 ? 's' : ''}</strong> to generate 
              a statistically significant profile. Keep using the Analyser to unlock your full Leadership Radar.
            </p>
          </div>

          {/* Recent Activity */}
          {recentAnalyses.length > 0 && (
            <div className="w-full mb-8">
              <div className="flex items-center justify-between mb-3 px-1">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                  Recent Submissions
                </h3>
                <button
                  onClick={() => navigate('/history')}
                  className="text-xs text-orange-600 dark:text-orange-500 font-medium hover:underline"
                >
                  View All
                </button>
              </div>
              
              <div className="flex flex-col gap-2">
                {recentAnalyses.slice(0, 3).map((analysis) => (
                  <div
                    key={analysis.id}
                    className="flex items-center p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border border-transparent hover:border-gray-200 dark:hover:border-gray-600 cursor-pointer"
                    onClick={() => navigate(`/analysis/${analysis.id}`)}
                  >
                    <div className={`h-8 w-8 rounded-full flex items-center justify-center mr-3 flex-shrink-0 ${
                      analysis.metadata?.source === 'email' 
                        ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400'
                        : 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                    }`}>
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        {analysis.metadata?.source === 'email' ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        )}
                      </svg>
                    </div>
                    
                    <div className="flex-1 text-left min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                          {analysis.metadata?.source === 'email' && analysis.metadata?.subject 
                            ? analysis.metadata.subject 
                            : `${analysis.text_snippet.substring(0, 30)}...`}
                        </p>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Analysed • {analysis.sentiment_summary && (
                          <span className="text-green-600 dark:text-green-400">{analysis.sentiment_summary}</span>
                        )}
                      </p>
                    </div>
                    
                    <span className="text-xs font-medium text-gray-400 bg-white dark:bg-gray-800 px-2 py-1 rounded flex-shrink-0 ml-2">
                      {new Date(analysis.date).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* CTA Button */}
          <button
            onClick={() => navigate('/analyse')}
            className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold py-3.5 px-6 rounded-full shadow-lg shadow-orange-500/25 transition-all transform hover:-translate-y-0.5 active:translate-y-0 flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Analyse New Communication
          </button>
        </div>
      </div>
    </div>
  );
};

